import { toJS } from 'mobx';

import type { Campaign, DisplayCreative } from '@feathr/blackbox';
import { AdTagCreative, CreativeClass } from '@feathr/blackbox';

import {
  hasValidAspectRatio,
  validateAdTagCreative,
} from '../AdWizardCreativesStep/AdWizardCreativesStep.utils';

export type TCreativeStatus = 'ready' | 'warning' | 'error';

/**
 * Derives the validation status for a single creative.
 *
 * @param creative - The creative to validate
 * @param campaign - The campaign the creative belongs to
 * @returns 'ready' when no errors, 'error' when validation failures exist
 */
export function getCreativeStatus(creative: DisplayCreative, campaign: Campaign): TCreativeStatus {
  const errors = getCreativeErrors(creative, campaign);
  return errors.length > 0 ? 'error' : 'ready';
}

/**
 * Returns the first validation error for a creative, or undefined if ready.
 *
 * @param creative - The creative to validate
 * @param campaign - The campaign the creative belongs to
 * @returns First error message, or undefined if valid
 */
export function getCreativeStatusTooltip(
  creative: DisplayCreative,
  campaign: Campaign,
): string | undefined {
  const errors = getCreativeErrors(creative, campaign);
  return errors.length > 0 ? errors[0] : undefined;
}

/**
 * Collects validation errors for a single creative.
 * Reuses the same validation logic as validateStepCreatives (per-creative loop).
 */
function getCreativeErrors(creative: DisplayCreative, campaign: Campaign): string[] {
  let errors: string[] = toJS(
    creative.validate(['width', 'height', 'spec', 'destination_url', 'name'], false).errors,
  );

  const validDimensions = creative.getValidDimensions(campaign);
  const specs = Object.values(validDimensions).map((v) => v.spec);
  const creativeIsValidDimensions = specs.includes(creative.get('spec'));
  const creativeHasValidAspectRatio = hasValidAspectRatio(creative, validDimensions);

  if (!creativeIsValidDimensions && !creativeHasValidAspectRatio) {
    errors.push(
      `${creative.get('width')}x${creative.get('height')} does not match any standard ad size. Images must have exact aspect ratios and meet minimum dimensions.`,
    );
  }

  if (
    creative instanceof AdTagCreative &&
    (creative as AdTagCreative).get('_cls') === CreativeClass.DisplayAdTag
  ) {
    errors = errors.concat(validateAdTagCreative(creative as AdTagCreative));
  }

  return errors;
}
